import { collectChargerOutputCircuits, chargerCircuitAllowancePct } from "../charger-output-circuits";
import { makeSystemCableSpecGetter } from "../cable-spec";
import { currentNeedsVerification, sustainedCableCurrent } from "../cable-current-evidence";
import type { DefLookup } from "../catalog";
import { DEFAULT_AUTO_SIZE_VDROP_TARGET } from "../settings/auto-size-vdrop-default";
import type { Issue, System } from "../types";
import { voltageDrop } from "../wire-tables";

/** Report the full external battery connection at conservative design currents,
 * never as a measurement or as simultaneous independent branch maxima. Manual
 * conductor declarations remain unchanged. Provisional estimates get a request
 * to verify, not an asserted actual violation or guaranteed lower bound. */
export function detectChargerPathVDrop(system: System, lookup: DefLookup, targetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET): Issue[] {
  const circuits = collectChargerOutputCircuits(system, lookup);
  if (!circuits.length) return [];
  const specOf = makeSystemCableSpecGetter(system, lookup, undefined, targetPct);
  const cables = new Map(system.cables.map(c => [c.id, c]));
  const nodes = new Map(system.nodes.map(n => [n.id, n]));
  const worstByPort = new Map<string, { pct: number; issue: Issue }>();
  for (const circuit of circuits) {
    const cableIds = [...new Set([...circuit.pos, ...circuit.neg])];
    let drop = 0, needsVerification = false, unsupported = false;
    for (const id of cableIds) {
      const cable = cables.get(id);
      if (!cable || !Number.isFinite(cable.lengthM) || cable.lengthM < 0) { unsupported = true; break; }
      const spec = specOf(cable);
      if (spec.isAc || (spec.sustainedCurrent == null && (spec.isCranking || (spec.startingCurrent ?? 0) > 0))) { unsupported = true; break; }
      needsVerification ||= currentNeedsVerification(spec) || !!spec.sizingVerificationRequired;
      drop += voltageDrop(sustainedCableCurrent(spec), cable.lengthM, spec.wire.mm2) * 0.5;
    }
    if (unsupported) continue;
    const allowance = chargerCircuitAllowancePct(circuit, targetPct), pct = drop / circuit.nominalV * 100;
    if (pct <= allowance + 1e-9 || (worstByPort.get(circuit.portId)?.pct ?? 0) >= pct) continue;
    const node = nodes.get(circuit.chargerId);
    const def = node?.kind === "component" ? lookup(node.definitionId, node.customDefinition) : undefined;
    const label = node?.label ?? def?.shortName ?? def?.name ?? "";
    const manufacturer = circuit.manufacturerAllowancePct != null && circuit.manufacturerAllowancePct <= targetPct;
    worstByPort.set(circuit.portId, { pct, issue: {
      code: "charger-cumulative-vdrop", severity: "warning",
      title: { key: needsVerification ? "diag.charger-cumulative-vdrop.title_verify" : "diag.charger-cumulative-vdrop.title", vars: { label, pct: pct.toFixed(1) } },
      message: { key: needsVerification ? "diag.charger-cumulative-vdrop.message_verify"
        : manufacturer ? "diag.charger-cumulative-vdrop.message_manufacturer" : "diag.charger-cumulative-vdrop.message_target",
        vars: { label, drop: drop.toFixed(2), allowance } },
      fix: { key: "diag.charger-cumulative-vdrop.fix" }, nodeIds: [circuit.chargerId], cableIds,
    } });
  }
  return [...worstByPort.values()].map(result => result.issue);
}
